import { useState, useEffect, useCallback } from "react";
import { Outlet, useOutletContext, useNavigate } from "react-router-dom";
import { BetaStrip } from "../components/shell/BetaStrip";
import { AppSidebar, type Team, type Project } from "../components/shell/AppSidebar";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Menu } from "lucide-react";

interface LayoutContext {
  refreshProjects: () => void;
  teams: Team[];
  projects: Record<string, Project[]>;
}

export function useLayoutContext() {
  return useOutletContext<LayoutContext>();
}

export function DashboardLayout() {
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [projects, setProjects] = useState<Record<string, Project[]>>({});
  const [mobileOpen, setMobileOpen] = useState(false);

  const refreshProjects = useCallback(() => {
    fetch("/api/teams")
      .then((r) => r.json())
      .then((data: unknown) => {
        const teamList = Array.isArray(data) ? (data as Team[]) : [];
        setTeams(teamList);
        teamList.forEach((team) => {
          fetch(`/api/teams/${team.id}/projects`)
            .then((r) => r.json())
            .then((projs: unknown) => {
              setProjects((prev) => ({
                ...prev,
                [team.id]: Array.isArray(projs) ? (projs as Project[]) : [],
              }));
            })
            .catch(() => {
              setProjects((prev) => ({ ...prev, [team.id]: [] }));
            });
        });
      })
      .catch(() => {
        setTeams([]);
      });
  }, []);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  const handleCreateProject = (teamId: string) => {
    setMobileOpen(false);
    navigate(`/teams/${teamId}?new=project`);
  };

  const handleCreateTeam = () => {
    setMobileOpen(false);
    navigate("/teams?new=team");
  };

  return (
    <div className="flex h-screen bg-paper text-foreground overflow-hidden">
      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[260px]">
          <AppSidebar
            teams={teams}
            projects={projects}
            onCreateProject={handleCreateProject}
            onCreateTeam={handleCreateTeam}
          />
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <AppSidebar
          teams={teams}
          projects={projects}
          onCreateProject={handleCreateProject}
          onCreateTeam={handleCreateTeam}
        />
      </div>

      <main className="flex-1 flex flex-col overflow-hidden min-w-0">
        <BetaStrip />
        <div className="flex md:hidden border-b border-rule px-3 py-2 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu className="size-5" />
          </Button>
          <span className="font-sans text-sm font-semibold">
            Swisstools<span className="text-faint">.dev</span>
          </span>
        </div>
        <div className="flex-1 flex flex-col overflow-auto min-w-0 min-h-0">
          <Outlet
            context={
              { refreshProjects, teams, projects } satisfies LayoutContext
            }
          />
        </div>
      </main>
    </div>
  );
}
